import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, IKpiViewModel, WidgetValues, IBindable, ErrorLevel, BusinessKpi, SelectorType } from '../../models';
import { KpiWithOption, IGroupService, IBusinessKpiService, IStateRedirectService, ISeriesService, ILoggerService } from '../../services';
import { groupSelected, wtDroppableDrop, refreshClick, WideError, missingSeries, tagDefaultState } from '../../common';
import { BusinessKpiWidget } from './businessKpi.widget';
import { BusinessKpiConfiguration } from './businessKpi.configuration';
import { IRootScope } from 'IRootScope';
import { Kpi } from '../../wtFramework';

class BusinessKpiController implements angular.IComponentController {
    isLoading: boolean;
    kpiList: IKpiViewModel[] = [];
    kpiVmList: KpiWithOption[] = [];
    loadingMessage: string;

    readonly boundData: WidgetValues;
    readonly isBound: boolean;
    readonly item: BusinessKpiWidget;
    readonly series: IKpiViewModel[] = [];

    private _seriesConfiguration: IBindable[];
    //TODO move statuses elsewhere
    private _statuses = ['Ok', 'Warning', 'Error'];

    static $inject = [
        '$rootScope',
        '$scope',
        '$translate',
        '$state',
        'groupService',
        'businessKpiService',
        'stateRedirectService',
        'seriesService',
        'loggerService'
    ];

    constructor(
        private _$rootScope: IRootScope,
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _$state: angular.ui.IStateService,
        private _groupService: IGroupService,
        private _businessKpiService: IBusinessKpiService,
        private _stateRedirectService: IStateRedirectService,
        private _seriesService: ISeriesService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this.isLoading = false;
        if (!this.isBound) {
            this._$scope.$watch(() => this.item, () => {
                if (this.item) {
                    this._$scope.$watch(
                        () => this.item.configuration.kpis,
                        newValue => this.updateSeries(this.item.configuration),
                        true
                    );
                    this._$scope.$watch(
                        () => this.item.configuration.nameConfiguration,
                        newValue => this.updateSeries(this.item.configuration),
                        true
                    );
                }
            });
            this._$scope.$on(groupSelected, () => {
                this.updateSeries(this.item.configuration);
            });
            this._$scope.$on(wtDroppableDrop, (event, data) => {
                if (data.itemId) {
                    const guid = data.itemId.replace('id', '');
                    this._businessKpiService
                        .getSeries(guid)
                        .then(config => this.item.configuration.kpis.push(config));
                }
            });
            this._$scope.$on(refreshClick, () => this.refresh());
        }
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['boundData']?.currentValue) {
            this.refreshBoundData(this.boundData);
        }

        if (changes['series']?.currentValue) {
            this.kpiList = this.series;
            if (this.boundData) {
                this.kpiVmList = this._businessKpiService.bindData(this.boundData, this.series);
            }
        }
    }

    private refreshBoundData(boundData: WidgetValues) {
        this.kpiVmList = this._businessKpiService.bindData(boundData, this.kpiList);
    }

    private updateSeries(configuration: BusinessKpiConfiguration) {
        if (configuration) {
            const newValue = configuration.kpis;
            if (newValue) {
                this.isLoading = true;
                this._seriesService
                    .getUpdatedGuids(newValue)
                    .then(data => {
                        this._seriesConfiguration = data;
                        return this._businessKpiService.getViewModels(data, configuration.nameConfiguration);
                    })
                    .then(converted => {
                        if (_.some(converted, c => !c.viewModel)) {
                            this.item.setError(new WideError(ErrorLevel.Error, missingSeries));
                        }
                        this.kpiList = _.sortBy(
                            converted.filter(c => !!c.viewModel),
                            c => _.findIndex(newValue, n => n.value + n.guid === c.kpi.identifier)
                        );
                        this.kpiVmList = this.kpiList.map(d => {
                            const result: any = angular.copy(d.kpi);
                            result.options = d.options;
                            return result;
                        });
                        if (!this.boundData) {
                            this.refresh();
                        } else {
                            this.refreshBoundData(this.boundData);
                        }
                    })
                    .catch(reason => this._loggerService.logWarning('Some series are incorectly bound in KPI widget'))
                    .finally(() => this.isLoading = false);
            }
        }
    }

    go(kpi: Kpi) {
        if (!this._$rootScope.isEditMode) {
            const original: any = _.find(this._seriesConfiguration, k => k.value + k.guid === kpi.identifier);
            if (original && original.kpiType === SelectorType.tagDefinition) {
                this._$state.go(tagDefaultState, { tagDefinitionGuid: original.guid });
            } else {
                this._groupService
                    .getIdentifier(original.guid)
                    .then(identifier => this._stateRedirectService.redirectIdentifier(identifier));
            }
        }
    }

    private refresh() {
        if (this._seriesConfiguration && this._seriesConfiguration.length > 0) {
            this.isLoading = true;
            this._businessKpiService
                .readData(this.item.configuration, this.item.interval(), this._seriesConfiguration)
                .then(values => {
                    this.mapValues(values);
                    this.kpiVmList = this.kpiList.map(d => {
                        const result: any = angular.copy(d.kpi);
                        result.options = d.options;
                        return result;
                    });
                })
                .finally(() => this.isLoading = false);
        }
    }

    private mapValues(values: BusinessKpi[]) {
        values.forEach(v => {
            const kpiViewModel = _.find(this.kpiList, k => k.kpi.identifier === (v.valueType ? v.valueType : 'average') + v.guid);
            if (kpiViewModel) {
                const kpi = kpiViewModel.kpi;
                const value = v.value;
                kpi.value = value;
                kpi.variation = v.tendency;
                kpi.quality = this._$translate.instant(this._statuses[v.quality]);
                kpi.context = v.context;
                kpi.comment = v.comment;
            }
        });
    }
}

export const wtBusinessKpi: ComponentDefinition = {
    componentId: 'wtBusinessKpi',
    controller: BusinessKpiController,
    template: require('./businessKpi.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<?',
        isBound: '<?',
        item: '<',
        ratio: '<?',
        series: '<?'
    }
};